import { API } from "@swivl-mono/api-helper";
import { Button, Spinner } from "@swivl-mono/ui";
import { useState } from "react";

import { AlertsModel } from "../../../Models/Notifications/Alerts.Model";
import { styles } from "../Onboarding/shared/onboarding.styles";

export function BillingView() {
  const [isProcessing, setIsProcessing] = useState(false);
  const [lastExportResult, setLastExportResult] = useState<string | null>(null);
  const [orgId, setOrgId] = useState("");
  const [isExportingLocations, setIsExportingLocations] = useState(false);

  const SHEET_ID = "1t5r3DMKUVjX-r9BtPu-Cnu4WMWhzH6kNqqBw2q3fHjI";
  const SHEET_NAME = "Latest";

  const exportToBillingSheet = async () => {
    setIsProcessing(true);
    setLastExportResult(null);

    try {
      const billingData = await API.Analytics.getBillingData();

      if (!billingData || billingData.length === 0) {
        AlertsModel.errorToast("No billing data found to export");
        return;
      }

      const clearRange = `${SHEET_NAME}!A1:Z1000`;
      await API.Google.updateGoogleSheet(SHEET_ID, clearRange, [[]]);

      const headers = [
        "Report Date",
        "Org ID",
        "Org Name",
        "Unique Users (Last 7 Days)",
        "Total Locations",
        "Non-Deleted Locations",
        "Live Locations",
        "SiteLink Locations",
        "SSMCloud Locations",
        "SSM Locations"
      ];

      const dataRows = billingData.map((org) => [
        org.snapshotDate,
        org.orgId.toString(),
        org.orgName,
        org.uniqueUsers.toString(),
        org.totalLocations.toString(),
        org.totalNonDeletedLocations.toString(),
        org.liveLocations.toString(),
        org.sitelink.toString(),
        org.ssmCloud.toString(),
        org.ssm.toString()
      ]);

      const allData = [headers, ...dataRows];

      const dataRange = `${SHEET_NAME}!A1:J${allData.length + 1}`;
      await API.Google.updateGoogleSheet(SHEET_ID, dataRange, allData);

      const resultMessage = `Successfully exported ${billingData.length} orgs to JSAS ('Latest' sheet)`;
      setLastExportResult(resultMessage);
      AlertsModel.successToast(resultMessage);
    } catch (error) {
      console.error("Error exporting to JSAS:", error);
      const errorMessage = error instanceof Error ? error.message : "Failed to export to JSAS";
      setLastExportResult(`Error: ${errorMessage}`);
      AlertsModel.errorToast(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const exportOrgLocationsToCSV = async () => {
    if (!orgId.trim()) {
      AlertsModel.errorToast("Please enter an org ID");
      return;
    }

    setIsExportingLocations(true);

    try {
      const locationData = await API.Analytics.getOrgLocationsExport(parseInt(orgId));

      if (!locationData || locationData.length === 0) {
        AlertsModel.errorToast("No location data found for this org");
        return;
      }

      const headers = ["Location Name", "Is Live", "Location Created", "Location Deleted", "Group Name", "FMS Type"];
      const csvContent = [
        headers.join(","),
        ...locationData.map((location) =>
          [
            `"${location.locationName}"`,
            location.isLive ? "Yes" : "No",
            location.locationCreated,
            location.locationDeleted || "",
            `"${location.groupName}"`,
            location.fmsType || ""
          ].join(",")
        )
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `org-${orgId}-locations-export.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      AlertsModel.successToast(`Exported ${locationData.length} locations for org ${orgId}`);
    } catch (error) {
      console.error("Error exporting org locations:", error);
      const errorMessage = error instanceof Error ? error.message : "Failed to export org locations";
      AlertsModel.errorToast(errorMessage);
    } finally {
      setIsExportingLocations(false);
    }
  };

  return (
    <div className="p-8">
      <div style={styles.container}>
        <h1 style={{ ...styles.heading, fontSize: "24px" }}>Billing</h1>

        <div style={styles.buttonGroup}>
          <Button onClick={exportToBillingSheet} disabled={isProcessing}>
            {isProcessing ? (
              <>
                <Spinner />
                {" Exporting to JSAS..."}
              </>
            ) : (
              "Export Billing Data to JSAS"
            )}
          </Button>
        </div>

        {lastExportResult && <div style={lastExportResult.startsWith("Error:") ? styles.errorBox : styles.successBox}>{lastExportResult}</div>}

        <a
          href="https://docs.google.com/spreadsheets/d/1t5r3DMKUVjX-r9BtPu-Cnu4WMWhzH6kNqqBw2q3fHjI/edit?gid=1242846598#gid=1242846598"
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-[#ff6528] hover:text-[#ff4b34]"
        >
          View JSAS →
        </a>
      </div>

      <div style={styles.container}>
        <h1 style={{ ...styles.heading, fontSize: "24px" }}>Location Export</h1>
        <div style={styles.inputWrapper}>
          <input
            id="orgId"
            type="number"
            value={orgId}
            onChange={(e) => setOrgId(e.target.value)}
            placeholder="Enter org ID"
            style={{ ...styles.input, width: "150px" }}
            className="focus:border-[#ff6528] focus:ring-2 focus:ring-[#ff6528]/20 focus:outline-none"
          />
        </div>
        <div style={styles.buttonGroup}>
          <Button onClick={exportOrgLocationsToCSV} disabled={isExportingLocations}>
            {isExportingLocations ? (
              <>
                <Spinner />
                {" Exporting..."}
              </>
            ) : (
              "Export to CSV"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
